import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core';
import { toPng } from 'html-to-image';
import jsPDF from 'jspdf';
import 'jspdf-autotable'
import { ChatbotChartCard } from './ChatbotChartCard';
import BarCharIcon from "assets/images/icons/bar-chart-icon.svg";
import PieChartIcon from "assets/images/icons/pie-chart-icon.svg";
import LineChartIcon from "assets/images/icons/line-chart-icon.svg";
import ComboChartIcon from "assets/images/icons/combo-chart-icon.svg";
import ExcelIcon from 'assets/images/excel.png';
import ImageIcon from 'assets/images/icons/image-icon.svg';
import PdfIcon from 'assets/images/icons/pdf-icon.svg';
import MaestroLogo from 'assets/images/maestro_logo_png_light.png';
import ChatLogo from 'assets/images/chatLogo2.png';
import { ChatProcessing } from './ChatProcessing';
import { checkAndFormatDate } from 'helpers/lisaHelpers';
import { exportChat } from './ChatHeaderMenu';

const useStyles = makeStyles(theme => ({
    root: {
        padding: `${theme.spacing(2)}px ${theme.spacing(2.5)}px`,
        color: theme?.palette?.black,
    },
    chartButtonsContainer: {
        paddingBottom: theme.spacing(2)
    },
    chartButton: {
        margin: `${theme.spacing(1)}px ${theme.spacing(0.5)}px`,
        borderRadius: theme.spacing(2.5),
        padding: theme.spacing(0.5, 1.5),
        display: 'inline-flex',
        alignItems: 'center',
        cursor: 'pointer',
        fontSize: theme.spacing(2),
        '& img': {
            marginRight: theme.spacing(0.5),
        },
        '&.combo': {
            '& img': {
                transform: 'rotate(-90deg)',
            },
        },
    },
    chartBtnLabel: {
        fontSize: theme.spacing(1.75),
    },
    hiddenChartContainer: {
        height: '1px',
        overflow: 'scroll'
    }

}));

const chartTypeToIcon = {
    bar: BarCharIcon,
    pie: PieChartIcon,
    line: LineChartIcon,
    combo: ComboChartIcon,
}

export const exportCsv = (tableData, dataKeys) => {
    const csvContent = [
        dataKeys.join(','), // header row
        ...tableData.map(row => dataKeys.map(key => `"${checkAndFormatDate(row[key])}"`).join(',')) // data rows
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.href = url;
    const curDate = new Date().toISOString().split('T')[0];
    link.download = `Lisa_chat_${curDate}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

export const ChatbotKpiWrapper = ({ chartTypes, ...rest }) => {
    const classes = useStyles();
    const chartRef = useRef(null);
    const chartRefPdf = useRef(null);
    const { t } = useTranslation();
    const [chartType, setChartType] = useState(null);
    const [isDownloading, setIsDownloading] = useState(false)
    const { tableData, dataKeys, message, params } = rest;

    const handleChartTypeChange = (type) => {
        setChartType(type);
        // simulate scroll to bottom of chat after chart is rendered
        setTimeout(() => {
            const chatBody = document.querySelector('.rcb-chat-body-container');
            const chartContainer = chartRef.current;
            if (!chatBody || !chartContainer) return;
            chatBody.scrollTo({
                top: chartContainer.offsetTop - 70,
                behavior: 'smooth'
            });
        }, 500);
    }

    const downloadChartImage = async () => {
        const chartContainer = chartRef.current;
        if (!chartContainer) return;

        {/*await document.fonts.ready;
        await new Promise(resolve => {
            requestAnimationFrame(() => {
                requestAnimationFrame(resolve);
            });
        });
        const canvas = await html2canvas(chartContainer, {
            scale: 1.5,
            useCORS: true,
            backgroundColor: "#ffffff"
        });

        const imgData = canvas.toDataURL("image/png");

        // Create download link
        const link = document.createElement("a");
        link.href = imgData;
        const curDate = new Date().toISOString().split('T')[0];
        link.download = `LISA_${chartType}_chart_${curDate}.png`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link); */}

        toPng(chartContainer, {
            pixelRatio: 1,
            cacheBust: true,
            backgroundColor: '#fff'
        }).then((dataUrl) => {
            const link = document.createElement('a');
            const curDate = new Date().toISOString().split('T')[0];
            link.download = `LISA_${chartType}_chart_${curDate}.png`;
            link.href = dataUrl
            link.click();
        }).catch((err) => {
            console.log(err)
        })
    };

    const chartMaxLengths = { bar: 20, pie: 5, line: 12, combo: 20 }
    const filterdChartTypes = chartTypes?.filter(type => {
        return rest?.chartData?.[type]?.data?.length <= chartMaxLengths[type]
    })

    const handlePdfExport = async () => {
        const chatData = [{
            metadata: {
                query_text: params?.userInput,
                contextualized_question: rest?.metadata?.contextualized_question ?? "",
                question_uuid: rest?.metadata?.question_uuid ?? ""
            },
            message,
            tableData,
        }];
        exportChat({
            chatData: chatData,
            setIsDownloading,
            includeCharts: true
        })
    }

    return (
        <div data-testid="chatbotKpiWrapper" className={`chatKpi ${classes.root}`}>
            <div className={classes.chartButtonsContainer}>
                {filterdChartTypes?.length > 0 && filterdChartTypes.map((type) => (
                    <button
                        variant="contained"
                        className={`${classes.chartButton} ${type}`}
                        key={type}
                        onClick={() => handleChartTypeChange(type)}
                        data-testid={`${type}ChartButton`}
                        size="small"
                    >
                        <img src={chartTypeToIcon[type]} alt="chart" />
                        {t(type)}
                    </button>
                )
                )}
                {['development', 'local'].includes(process.env.REACT_APP_ENV) && (<button
                    className={`${classes.chartButton} pdf`}
                    onClick={handlePdfExport}
                    data-testid="exportPdf"
                >
                    <img src={PdfIcon} alt="" />
                    {t('export')}
                </button>)}

                {tableData && tableData.length && (
                    <button
                        className={`${classes.chartButton} excel`}
                        onClick={() => exportCsv(tableData, dataKeys)}
                        data-testid="exportCsv"
                    >
                        <img src={ExcelIcon} alt="" />
                        {t('export')}
                    </button>
                )}

                {chartType && (
                    <button
                        className={`${classes.chartButton} downloadImage`}
                        onClick={downloadChartImage}
                        data-testid="downloadChart"
                    >
                        <img src={ImageIcon} alt="" />
                        {t('export')}
                    </button>
                )}

            </div>

            {isDownloading && <ChatProcessing chatProcess={<b>{t('your_download_will_begin_shortly')}...</b>} />}

            <ChatbotChartCard id="chatbotChartCard" chartType={chartType} ref={chartRef} {...rest} />
            <div className={`${classes.hiddenChartContainer} hiddenChartContainer`} ref={chartRefPdf}>
                {filterdChartTypes?.length > 0 && filterdChartTypes?.map(type => {
                    return <ChatbotChartCard id={`${type}ChartCard`} chartType={type} {...rest} key={type} />
                })}
            </div>
        </div >
    )
}